import React, { useState, useEffect } from 'react'

import useScrollReset from '../../useScrollReset'

import LoadingPlaceholder from 'components/LoadingPlaceholder'
import { Flex } from 'components/Box'
import { Layout } from '../../components/Layout'
import { TextAlignment } from 'modules/shares/types'
import InfiniteList, { handleFetchMore } from 'components/InfiniteList'
import EmptyState from 'modules/shares/components/EmptyState'

import { ListGrid } from '../../components/Grid'
import {
  TextItem,
  TextImageWrapper,
  TextImage,
  TextName,
  TextDetail,
  TextDetailName,
  TextAlignImg,
} from './TextStyles.styles'

import { useGetTextStyles } from '../../operations'
import useResponsiveImage from '../../useResponsiveImage'
import { useSearchComponentsContext } from '../../SearchCwvContext'

// This is a valid use case to validate TextStyle
// eslint-disable-next-line no-restricted-imports
import { TextStyle } from 'gqlTypes/expansiveTypes'

import type { File } from '../../types'

// Text Align Images
import alignCenter from 'icons/inspector-text-align-center.svg'
import alignJustify from 'icons/inspector-text-align-justify.svg'
import alignLeft from 'icons/inspector-text-align-left.svg'
import alignMiddle from 'icons/inspector-text-align-middle.svg'
import alignTop from 'icons/inspector-text-align-top.svg'

const renderAlignmentImage = (alignment: TextAlignment) => {
  switch (alignment) {
    case TextAlignment.Bottom:
      return (
        <TextAlignImg src={alignTop} style={{ transform: 'rotate(180deg)' }} />
      )
    case TextAlignment.Top:
      return <TextAlignImg src={alignTop} />
    case TextAlignment.Right:
      return (
        <TextAlignImg src={alignLeft} style={{ transform: 'scaleX(-1)' }} />
      )
    case TextAlignment.Left:
      return <TextAlignImg src={alignLeft} />
    case TextAlignment.Middle:
      return <TextAlignImg src={alignMiddle} />
    case TextAlignment.Center:
      return <TextAlignImg src={alignCenter} />
    case TextAlignment.Justified:
      return <TextAlignImg src={alignJustify} />
    default:
      return null
  }
}

const TextStyleItem = ({ textStyle }: { textStyle: TextStyle }) => {
  const imageProps = useResponsiveImage({
    name: textStyle.name,
    files: textStyle.files as File[],
  })

  return (
    <TextItem key={textStyle.identifier}>
      <TextName>{textStyle.name}</TextName>
      <TextImageWrapper>
        <TextImage {...imageProps} />
      </TextImageWrapper>
      <Flex mt={2}>
        <TextDetailName>Name</TextDetailName>
        <TextDetail>
          {renderAlignmentImage(TextAlignment.Left)} Font (Weight) — 13
        </TextDetail>
      </Flex>
    </TextItem>
  )
}

const TextStylesList = ({
  searchCwv,
  path,
}: {
  searchCwv: string
  path: string | null
}) => {
  const { entries, meta, fetchMore, loading, filter } = useGetTextStyles(
    searchCwv
  )
  useScrollReset()

  useEffect(() => {
    filter(path === 'View All' ? null : path)
    // we do NOT want to add filter as a dependency here
    // eslint-disable-next-line
  }, [path])

  if (loading) {
    return (
      <Flex justifyContent="center" flex="auto">
        <LoadingPlaceholder size="64px" />
      </Flex>
    )
  }

  if (entries.length === 0) {
    if (searchCwv !== '') {
      return (
        <EmptyState
          title="No search results"
          description="No matching items."
          icon="search"
        />
      )
    }

    return (
      <EmptyState
        title="No Text Styles"
        description="This Document does not contain any Text Styles."
        icon="textStyle"
      />
    )
  }

  return (
    <InfiniteList
      onLoadMore={handleFetchMore(
        fetchMore,
        ['share', 'version', 'document', 'components', 'entries'],
        { after: meta?.after || null }
      )}>
      <ListGrid>
        {entries.map(textStyle => (
          <TextStyleItem key={textStyle.identifier} textStyle={textStyle} />
        ))}
      </ListGrid>
    </InfiniteList>
  )
}

export const TextStyles: React.FC = () => {
  const { searchCwv } = useSearchComponentsContext()
  const [selected, setSelected] = useState('View All')

  return (
    <Layout onSelect={setSelected} type="TEXT_STYLE" selected={selected}>
      <TextStylesList searchCwv={searchCwv} path={selected} />
    </Layout>
  )
}
